import React, { useState } from 'react';
import { Smartphone, Landmark } from 'lucide-react';

/** Platform-wide providers only. NCBA Till is event-scoped (e.g. Tabasamu Festival). */
export const PAYMENT_PROVIDERS = {
  MPESA_DARAJA: {
    id: 'MPESA_DARAJA',
    label: 'M-Pesa STK',
    shortLabel: 'M-Pesa STK (Daraja)',
    description: 'Direct Safaricom STK push. Default for all events without a custom gateway.',
    icon: Smartphone
  },
  FAMILY_BANK: {
    id: 'FAMILY_BANK',
    label: 'Family Bank STK',
    shortLabel: 'M-Pesa via Family Bank',
    description: 'M-Pesa collections through Family Bank Open Banking for standard events.',
    icon: Landmark
  }
};

export function getPaymentProviderMeta(providerId) {
  return PAYMENT_PROVIDERS[providerId] || PAYMENT_PROVIDERS.MPESA_DARAJA;
}

export default function PaymentProviderSwitcher({
  value = 'MPESA_DARAJA',
  onChange,
  disabled = false,
  saving = false,
  mpesaConfigured = true,
  familyBankConfigured = false
}) {
  const [pending, setPending] = useState(null);
  const active = pending || (PAYMENT_PROVIDERS[value] ? value : 'MPESA_DARAJA');

  const handleSelect = async (providerId) => {
    if (disabled || saving || providerId === value) return;
    setPending(providerId);
    try {
      await onChange(providerId);
    } finally {
      setPending(null);
    }
  };

  const isConfigured = (id) => {
    if (id === 'MPESA_DARAJA') return mpesaConfigured;
    if (id === 'FAMILY_BANK') return familyBankConfigured;
    return false;
  };

  const configHint = (id) => {
    if (id === 'MPESA_DARAJA') return 'Configure Daraja credentials in M-Pesa Daraja settings';
    if (id === 'FAMILY_BANK') return 'Add credentials in Family Bank STK settings first';
    return '';
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.values(PAYMENT_PROVIDERS).map((option) => {
          const Icon = option.icon;
          const isActive = active === option.id;
          const configured = isConfigured(option.id);

          return (
            <button
              key={option.id}
              type="button"
              disabled={disabled || saving}
              onClick={() => handleSelect(option.id)}
              className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                isActive
                  ? 'border-orange-500 bg-orange-50/60 shadow-sm ring-1 ring-orange-200'
                  : 'border-gray-200 bg-white hover:border-orange-200 hover:bg-orange-50/20'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                    isActive ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">{option.label}</span>
                    {isActive && (
                      <span className="inline-flex rounded-full bg-orange-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                        Active for standard events
                      </span>
                    )}
                    {option.id === 'MPESA_DARAJA' && (
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500 leading-snug">{option.description}</p>
                  {!configured && (
                    <p className="mt-2 text-xs font-medium text-amber-700">
                      {configHint(option.id)}
                    </p>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">
        NCBA Till is not a platform-wide option. Events that settle to an organiser till
        (currently Tabasamu Festival) use NCBA automatically at checkout.
      </p>
    </div>
  );
}
